import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { PinOff } from 'lucide-react'
import { ViewIcon } from './viewIcons'
import { countViewTasks } from './viewTaskCounts'
import type { FilterContext } from '@/lib/filters/applyFilters'
import type { SavedFilterDocument, StarredDocument } from '@/lib/db/rxdb/hooks'
import type { CustomFieldValueDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-values'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from '@/components/ui/sidebar'
import {
  useCustomFieldValues,
  useCycleWorkItems,
  useProjects,
  useSavedFilters,
  useStarredMutations,
  useStarredSavedFilters,
  useTaskLabels,
  useTasks,
} from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import useLoginStore from '@/store/LoginStore'
import { cn } from '@/lib/utils'

interface PinnedView {
  view: SavedFilterDocument
  star: StarredDocument
  path: string
  count: number
}

/**
 * "Pinned views" (design 20b): the saved views this user pinned from the Save
 * as view popover, each with its live task count. Pinning rides on the
 * existing favourites (starred items with entityType 'saved_filter'), so it
 * syncs per user. Self-contained so the sidebar can mount it under Favorites.
 */
export function PinnedViewsSection({
  workspaceSlug,
  className,
}: {
  workspaceSlug: string
  className?: string
}) {
  const { t } = useTranslation('tasks')
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const userId = useLoginStore((s) => s.userId)

  const { starredItems } = useStarredSavedFilters()
  const { savedFilters } = useSavedFilters()
  const { projects } = useProjects({ isDeleted: false })
  const { tasks } = useTasks({ isDeleted: false })
  const { taskLabels } = useTaskLabels()
  const { cycleWorkItems } = useCycleWorkItems()
  const { customFieldValues } = useCustomFieldValues()
  const {
    open: { statusIds: openStatusIdList },
  } = useStatuses()
  const { deleteStar } = useStarredMutations()

  const context = useMemo<FilterContext>(() => {
    const taskLabelMap = new Map<string, Set<string>>()
    for (const tl of taskLabels) {
      if (tl.isDeleted) continue
      const set = taskLabelMap.get(tl.taskId)
      if (set) set.add(tl.labelId)
      else taskLabelMap.set(tl.taskId, new Set([tl.labelId]))
    }
    const taskCycleMap = new Map<string, Set<string>>()
    for (const cw of cycleWorkItems) {
      if (cw.isDeleted) continue
      const set = taskCycleMap.get(cw.workItemId)
      if (set) set.add(cw.cycleId)
      else taskCycleMap.set(cw.workItemId, new Set([cw.cycleId]))
    }
    const customFieldValueMap = new Map<string, Map<string, CustomFieldValueDocument>>()
    for (const v of customFieldValues) {
      if (v.isDeleted) continue
      let fields = customFieldValueMap.get(v.taskId)
      if (!fields) {
        fields = new Map()
        customFieldValueMap.set(v.taskId, fields)
      }
      fields.set(v.customFieldDefinitionId, v)
    }
    return { taskLabelMap, taskCycleMap, customFieldValueMap }
  }, [taskLabels, cycleWorkItems, customFieldValues])

  const openStatusIds = useMemo(() => new Set(openStatusIdList), [openStatusIdList])

  const pinned = useMemo<Array<PinnedView>>(() => {
    if (starredItems.length === 0) return []
    const views = new Map(savedFilters.map((v) => [v.id, v]))
    const byId = new Map(projects.map((p) => [p.id, p]))

    const pathFor = (view: SavedFilterDocument): string | null => {
      const owner = view.scopeEntityId ? byId.get(view.scopeEntityId) : undefined
      if (!owner) return null
      const key = owner.projectKey.toLowerCase()
      if (view.scope === 'space') return `/${workspaceSlug}/spaces/${key}/views/${view.id}`
      if (view.scope !== 'project') return null
      const space = owner.parentProjectId ? byId.get(owner.parentProjectId) : undefined
      return space
        ? `/${workspaceSlug}/spaces/${space.projectKey.toLowerCase()}/projects/${key}/views/${view.id}`
        : `/${workspaceSlug}/projects/${key}/views/${view.id}`
    }

    const out: Array<PinnedView> = []
    for (const star of starredItems) {
      const view = views.get(star.entityId)
      if (!view || view.isDeleted) continue
      if (view.permission === 'private' && view.ownerId !== userId) continue
      const path = pathFor(view)
      if (!path) continue
      out.push({
        view,
        star,
        path,
        count: countViewTasks(view, tasks, projects, openStatusIds, context),
      })
    }
    return out
  }, [starredItems, savedFilters, projects, tasks, openStatusIds, context, userId, workspaceSlug])

  if (pinned.length === 0) return null

  return (
    <SidebarGroup className={className} data-testid="pinned-views">
      <SidebarGroupLabel className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {t('savedFilters.pinned.heading')}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <ul className="space-y-0.5">
          {pinned.map(({ view, star, path, count }) => {
            const active = pathname === path
            return (
              <li key={star.id} className="group/pinned relative">
                <button
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => void navigate({ to: path })}
                  className={cn(
                    'flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    active
                      ? 'bg-sidebar-active font-medium text-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded',
                      active ? 'bg-brand-tint text-brand' : 'text-muted-foreground',
                    )}
                  >
                    <ViewIcon icon={view.icon} />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{view.name}</span>
                  <span
                    className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground group-hover/pinned:opacity-0 group-focus-within/pinned:opacity-0"
                    aria-label={t('savedFilters.pinned.count', { count })}
                  >
                    {count}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={t('savedFilters.pinned.unpin', { name: view.name })}
                  onClick={() => void deleteStar(star.id)}
                  className="absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-sidebar-accent hover:text-foreground focus-visible:opacity-100 group-hover/pinned:opacity-100 [@media(hover:none)]:opacity-100"
                >
                  <PinOff className="size-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
